// context/SocketContext.js
// Manages a single Socket.io connection for the whole app.
// Components subscribe to socket events via useSocket().
//
// The socket connects once on login and disconnects on logout.
// SocketProvider must be inside AuthProvider so it can read sessionStorage auth.

import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { io } from 'socket.io-client';

// Derive socket server URL from REACT_APP_URL by stripping the /api suffix.
// In production: https://medicalyog.com/api → https://medicalyog.com
// In dev:        http://localhost:5000/api   → http://localhost:5000
function getSocketUrl() {
  const base = process.env.REACT_APP_URL || '';
  const stripped = base.replace(/\/api\/?$/, '');
  return stripped || window.location.origin;
}

const SOCKET_URL = getSocketUrl();

const SocketContext = createContext(null);

export function SocketProvider({ children }) {
  const socketRef  = useRef(null);
  const [connected, setConnected] = useState(false);

  // connect — creates a new socket with current credentials.
  const connect = useCallback(() => {
    var authHints = (function() { try { return JSON.parse(localStorage.getItem('auth') || '{}'); } catch(e) { return {}; } })();
    var token    = authHints.token || sessionStorage.getItem('token') || '';
    var username = authHints.username || sessionStorage.getItem('lu') || '';
    if (!username) return;
    if (socketRef.current?.connected) return; // already connected

    const sock = io(SOCKET_URL, {
      auth: { token, username },
      withCredentials: true,  // send httpOnly cookie for new cookie-based sessions
      transports: ['websocket', 'polling'],
      reconnectionAttempts: 8,
      reconnectionDelay:    2000,
      timeout:              10000,
    });

    sock.on('connect',    () => setConnected(true));
    sock.on('disconnect', () => setConnected(false));
    sock.on('connect_error', (err) => {
      // Auth failure — don't keep retrying indefinitely
      if (err.message === 'Unauthorized') sock.disconnect();
    });

    socketRef.current = sock;
    setConnected(false); // optimistic: will become true on 'connect' event
  }, []);

  // disconnect — cleanly closes the socket (called on logout).
  const disconnect = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }
    setConnected(false);
  }, []);

  // Auto-connect on mount if already authenticated (handles page refresh).
  useEffect(() => {
    connect();
    return () => {
      // Don't disconnect on unmount — SocketProvider lives for the app lifetime.
      // Explicit disconnect is called by logout handlers.
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // emit — safe wrapper; silently drops if socket is not connected.
  const emit = useCallback((event, data) => {
    socketRef.current?.emit(event, data);
  }, []);

  // on / off — subscribe / unsubscribe to socket events.
  // Components should call these inside useEffect with proper cleanup.
  const on = useCallback((event, handler) => {
    socketRef.current?.on(event, handler);
  }, []);

  const off = useCallback((event, handler) => {
    socketRef.current?.off(event, handler);
  }, []);

  return (
    <SocketContext.Provider value={{ socketRef, connected, connect, disconnect, emit, on, off }}>
      {children}
    </SocketContext.Provider>
  );
}

export const useSocket = () => useContext(SocketContext);
